import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SHARED_MODULES } from '@app/shared';
import { SelectGroupComponent } from '@app/shared/modules/select-group/select-group.component';
import { SelectSubjectComponent, SelectSubjectData } from '@app/shared/modules/select-subject/select-subject.component';
import { buildEntities, currentAcademicPeriod, preventWeekend } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { startOfDay } from 'date-fns';
import { cloneDeep, first, isEmpty, isEqual, size } from 'lodash';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, scan, tap } from 'rxjs/operators';
import { FormBase, SidenavDailyShow } from './base';
import { SequenceStateService } from './sequence-state.service';

export interface SidenavDailyData {
  entityId: number;
  groupId: number;
  subjectId: number;
  date: Date;
}
@Component({
  selector: 'app-sidenav-daily',
  imports: [SHARED_MODULES, SelectGroupComponent, SelectSubjectComponent],
  templateUrl: './sidenav-daily.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavDailyComponent implements OnInit, OnDestroy {
  private readonly _destroy = inject(DestroyRef);
  private readonly _store = inject(Store<AppState>);
  private readonly _cdref = inject(ChangeDetectorRef);
  private readonly _formBuilder = inject(FormBuilder);
  private stateService = inject(SequenceStateService);
  @Input() public statusError = false;
  @Input() public startDate = new Date();
  @Input() public targets: SidenavDailyShow[] = [];
  @Input() public multiple = true;
  @Input() public entityId: number;
  @Input() public groupId: number;
  @Input() public subjectId: number;
  @Output() public selected = new EventEmitter<SidenavDailyShow[]>();
  @Output() public selectedForm = new EventEmitter<any>();
  @Output() public formEdit = new EventEmitter<FormGroup>();
  public instance: FormBase;
  public form: FormGroup;
  public entities$: Observable<any[]>;
  public academicPeriod$: Observable<{ start: Date; end: Date }>;
  public subjectFilter$: Observable<SelectSubjectData>;
  public submitted$ = new BehaviorSubject<boolean>(false);
  private _groupSelected$ = new BehaviorSubject(undefined);
  private _academicPeriod$ = new BehaviorSubject(undefined);
  private _subjectSelected$ = new BehaviorSubject(undefined);
  private _entitySelected$ = new BehaviorSubject(undefined);

  constructor() {
    this.academicPeriod$ = this._academicPeriod$.asObservable();
    this.loadFilter();
    this.loadSelectedForm();
  }

  @Input() public set config(value: FormBase) {
    this.instance = value;
    this.checkerTargets();
  }

  public ngOnInit() {
    this.initForm();
    this.load();
  }

  public ngOnDestroy() {
    this.submitted$.complete();
    this._entitySelected$.complete();
    this._groupSelected$.complete();
    this._subjectSelected$.complete();
    this._academicPeriod$.complete();
  }

  public onAppend(form: FormGroup) {
    if (form.valid) {
      if (!this.submitted$.value) {
        const target = this.helperData(
          form.getRawValue(),
          this._entitySelected$.value,
          this._groupSelected$.value,
          this._subjectSelected$.value,
        );

        this.append(form, target);
      }
    } else {
      form.markAllAsTouched();
    }
  }

  public onRemove(target: SidenavDailyShow, index: number) {
    this.targets = this.targets.filter((_, i) => i !== index);
    this.selected.emit(cloneDeep(this.targets));
  }

  public onChangeEntity(value: any, entities: any[] = [], reset = true) {
    const entity = entities.find(({ id }) => id === value);
    if (reset) {
      this.form.reset({
        entityId: value,
        date: preventWeekend(this.startDate),
      });
    }
    this._entitySelected$.next(entity);
    this._groupSelected$.next(undefined);
  }

  public onChangeGroup(data: any) {
    this.form.get('groupId').enable();
    if (!(isEmpty(data.elements) || this.showField(data.elements))) {
      this.form.get('groupId').reset({ value: first<any>(data.elements)?.id, disabled: true });
    }
    this._groupSelected$.next(data);
  }

  public onChangeSubject(data: any) {
    this.form.get('subjectId').enable();
    if (!(isEmpty(data.elements) || this.showField(data.elements))) {
      this.form.get('subjectId').reset({ value: first<any>(data.elements)?.id, disabled: true });
    }
    this._subjectSelected$.next(data);
  }

  public markAllAsTouched() {
    this.form.markAllAsTouched();
    this._cdref.markForCheck();
  }

  public showField(data: any[] = []): boolean {
    return size(data) > 1;
  }

  public refresh() {
    this.checkerTargets();
  }

  private append(form: FormGroup, value: SidenavDailyShow) {
    if (!this.targets.some(({ id, ...t }) => isEqual(t, value))) {
      this.targets = [...(this.targets || []), value];
    }

    this.selected.emit(cloneDeep(this.targets));
    this.resetForm(form, this._groupSelected$.value, this._subjectSelected$.value);
  }

  private resetForm(form: FormGroup<any>, group: any, subject: any) {
    if (isEmpty(group.elements) || this.showField(group.elements)) {
      form.get('groupId').reset();
    }
    if (isEmpty(subject.elements) || this.showField(subject.elements)) {
      form.get('subjectId').reset();
    }
  }

  private initForm() {
    this.form = this._formBuilder.group({
      entityId: [null, Validators.required],
      groupId: [null, Validators.required],
      subjectId: [null, Validators.required],
      date: [preventWeekend(this.startDate), Validators.required],
    });
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this._destroy), distinctUntilChanged(isEqual), debounceTime(500))
      .subscribe((value) => this.stateService.updateFilters(value.date, value.groupId, value.subjectId));

    this.formEdit.emit(this.form);
  }

  private load() {
    this.entities$ = this._store.select(AppSelectors.appFeature).pipe(
      takeUntilDestroyed(this._destroy),
      filter((store) => !!store.usuario_ativo),
      tap(({ usuario_ativo }) => {
        this._academicPeriod$.next(currentAcademicPeriod(usuario_ativo));
        this.form.patchValue({
          entityId: this.entityId ?? usuario_ativo.entidade_id,
          groupId: this.groupId,
          subjectId: this.subjectId,
        });
      }),
      map((store) => {
        const entities = buildEntities(store);
        this.onChangeEntity(this.entityId ?? store.usuario_ativo.entidade_id, entities, false);
        return entities;
      }),
    );
  }

  private helperData(data: any, entity: any, group: any, subject: any): SidenavDailyShow {
    return {
      entity: {
        id: data.entityId,
        description: entity.name,
      },
      grade: {
        id: group?.result?.serie_id,
        description: group?.result?.serie?.descricao,
      },
      group: {
        id: data.groupId,
        description: group?.result?.description,
      },
      subject: {
        id: data.subjectId,
        description: subject?.result?.descricao,
      },
      date: startOfDay(data.date),
    };
  }

  private checkerTargets() {
    if (!isEmpty(this.targets)) {
      this.submitted$.next(true);
      const targets$ = this.targets.map((target) => of(target));

      merge(...targets$)
        .pipe(
          scan((acc, curr) => [...acc, curr], []),
          finalize(() => this.submitted$.next(false)),
        )
        .subscribe((res) => this.selected.emit(cloneDeep(res)));
    }
  }

  private loadFilter() {
    this.subjectFilter$ = combineLatest([this._groupSelected$, this._entitySelected$]).pipe(
      takeUntilDestroyed(this._destroy),
      map(([filter, entity]) => {
        if (!!filter?.result && !!entity) {
          return {
            systemType: entity.systemType || 3,
            groupId: filter.result.id,
            gradeId: filter.result.serie_id,
          };
        }
        return null;
      }),
    );
  }

  private loadSelectedForm() {
    combineLatest([this._entitySelected$, this._groupSelected$, this._subjectSelected$])
      .pipe(
        takeUntilDestroyed(this._destroy),
        filter(([entity, group, subject]) => !!this.form && !!entity && !!group && !!subject),
        map((res) => this.helperData(this.form.getRawValue(), ...res)),
      )
      .subscribe((res) => this.selectedForm.emit(res));
  }
}
